import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { first, includes, merge } from "lodash";
import { firstValueFrom, lastValueFrom } from "rxjs";

import { LgConsole } from "@logex/framework/core";
import {
    LG_APP_CONFIGURATION,
    LG_APP_INFO,
    LG_AUTHENTICATION_SERVICE,
    LG_AUTHORIZATION_SERVICE,
    LG_USER_INFO,
    LgAuthorizationServiceV2,
    OidcAuthService,
    UserSettingsService,
    WellKnownSettingCode
} from "@logex/framework/lg-application";
import { LG_LOCALIZATION_SETTINGS } from "@logex/framework/lg-localization";
import { LgConfirmationDialog } from "@logex/framework/ui-core";
import { urlConcat } from "@logex/framework/utilities";
// import { AppLgLocalizationSettings } from "./app-lg-localization-settings";
import { cloneDeep } from "lodash-es";
import { AppUser } from "../app-user";
import { AppLgLocalizationSettings } from "./app-lg-localization-settings";
import {
    AppCombinedConfiguration,
    AppConfiguration,
    AppDynamicConfiguration,
    AppStaticConfiguration
} from "./app-startup.service.types";

declare let APP_VERSION: string;
@Injectable({ providedIn: "root" })
export class AppStartupService {
    private _alert = inject(LgConfirmationDialog);
    private _appConfiguration = inject<AppConfiguration>(LG_APP_CONFIGURATION);
    private _appInfo = inject(LG_APP_INFO);
    private _authorizationService = inject<LgAuthorizationServiceV2>(LG_AUTHORIZATION_SERVICE);
    private _http = inject(HttpClient);
    private _lgConsole = inject(LgConsole);
    private _localizationSettings = inject<AppLgLocalizationSettings>(LG_LOCALIZATION_SETTINGS);
    private _oidcAuthService = inject<OidcAuthService>(LG_AUTHENTICATION_SERVICE);
    private _userInfo = inject<AppUser>(LG_USER_INFO);
    private _userSettingsService = inject(UserSettingsService);

    async initialize(): Promise<void> {
        try {
            this._lgConsole.debug("INIT...");

            const basePath = "/";
            this._addBaseHref(basePath);

            // TODO: Configure fall-back language first
            const staticConfig = await this._loadStaticConfiguration();

            if (!(await this._authenticate(staticConfig, basePath))) {
                // Authentication is not finished yet - redirect required
                return;
            }

            const dynamicConfig = await this._loadConfiguration(staticConfig);

            const config = merge(
                cloneDeep(staticConfig),
                dynamicConfig
            ) as AppCombinedConfiguration;
            this._lgConsole.debug("Full configuration", config);

            this._fillAppConfiguration(config);

            this._configureAppInfo();

            this._authorizationService.initialize({
                product: "Product.AppropriateCareMonitor",
                instance: "ApplicationInstance.ACM2025NL",
                url: staticConfig.services.authorization // FIXME
            });

            this._userSettingsService.initialize({
                instance: config.instance,
                environment: config.environment,
                url: config.services.userSettings
            });

            await this._configureLocalization(staticConfig);
            await this._fetchUserInfo();
        } catch (e) {
            this._lgConsole.error("Error initializing application", e);

            // TODO: Make normal error reporting
            await this._alert.alert("Error initializing application", e.message, {
                allowClose: false
            });

            throw e;
        }
    }

    private _addBaseHref(basePath: string): void {
        let baseEl: HTMLBaseElement = first(document.head.getElementsByTagName("base"));

        if (baseEl == null) {
            baseEl = document.createElement("base");
            baseEl.setAttribute("href", basePath);
            document.head.appendChild(baseEl);
        }
    }

    private _configureAppInfo(): void {
        Object.assign(this._appInfo, {
            productId: "Doelmatigheidsmonitor",
            versionNumber: APP_VERSION, // version from Directory.Build.props
            toolInstanceName: this._appConfiguration.instance,
            environment: this._appConfiguration.environment
            // userManualUrl: "https://support.logex.com/nl/financieel-besturingsmodel/begrotingsmodel"
            // fullAppName: string;
            // overrideCurrencyPrefix: string;
            // overrideCurrencySuffix: string;
        });
    }

    private async _loadStaticConfiguration(): Promise<AppStaticConfiguration> {
        // const config = await this._loadJsonFile<AppStaticConfiguration>("appsettings.json", false);

        const [config1, config2] = await Promise.all([
            this._loadJsonFile<AppStaticConfiguration>("appsettings.json", false),
            this._loadJsonFile<Partial<AppStaticConfiguration>>("appsettings.override.json", true)
        ]);

        const config = merge(config1, config2);

        // TODO: validate config

        // eslint-disable-next-line @typescript-eslint/dot-notation
        delete config["$schema"];
        this._lgConsole.debug("Static configuration received", config);
        return config;
    }

    private async _loadJsonFile<T>(url: string, ignoreNotFound: boolean): Promise<T> {
        let configText: string;
        try {
            configText = await lastValueFrom(this._http.get(url, { responseType: "text" }));
        } catch (e) {
            if ("status" in e && e.status === 404 && ignoreNotFound) {
                return null;
            } else {
                throw e;
            }
        }

        if (configText) {
            // Strip line comments (in a very dumb way) and parse JSON
            return JSON.parse(configText.replace(/^\s*\/\/.*$/gm, ""));
        } else if (ignoreNotFound) {
            return null;
        } else {
            throw Error("Empty configuration received");
        }
    }

    private async _configureLocalization(config: AppStaticConfiguration): Promise<void> {
        this._localizationSettings.availableLanguages = config.availableLanguages.split(",");
        this._localizationSettings.fallbackLanguage = config.fallbackLanguage;
        this._localizationSettings.preferredLanguage = config.defaultLanguage;
        this._localizationSettings.locale = config.defaultLanguage;
        this._localizationSettings.currency = config.currency;

        // Get user's language
        const uiSettings = first(
            await firstValueFrom(
                this._userSettingsService.get({ storageId: WellKnownSettingCode.uiSettings })
            )
        );

        if (uiSettings != null && uiSettings.value != null) {
            const lang = uiSettings.value.language;
            if (includes(this._localizationSettings.availableLanguages, lang)) {
                this._localizationSettings.preferredLanguage = lang;
                this._localizationSettings.locale = lang;
            }
        }

        this._localizationSettings.setReady();
    }

    private async _authenticate(
        config: AppStaticConfiguration,
        basePath: string
    ): Promise<boolean> {
        this._oidcAuthService.configure({
            authority: config.services.authentication.authority,
            clientId: config.services.authentication.clientId,
            audience: config.services.authentication.audience,
            type: config.services.authentication.type ?? "generic",
            basePath,
            redirectUrl: window.location.href
        });
        const authenticated = await this._oidcAuthService.login();

        if (!authenticated) return false;

        this._userInfo.id = parseInt(this._oidcAuthService.user.id);
        return true;
    }

    private async _loadConfiguration(
        config: AppStaticConfiguration
    ): Promise<AppDynamicConfiguration> {
        const data = await lastValueFrom(
            this._http.get<AppDynamicConfiguration>(
                urlConcat(config.services.configuration, "/configuration"),
                {
                    params: { instance: config.instance, environment: config.environment },
                    headers: { "api-version": "1.0" }
                }
            )
        );

        this._lgConsole.debug("Dynamic configuration received", data);

        return data;
    }

    private _fillAppConfiguration(config: AppCombinedConfiguration): void {
        this._appConfiguration.full = config;

        this._appConfiguration.instance = config.instance;
        this._appConfiguration.environment = config.environment;
        this._appConfiguration.applicationInsightsInstrumentationKey =
            config.applicationInsights?.instrumentationKey;

        this._appConfiguration.testMachine = "";
        const environment = config.environment?.toLowerCase();
        if (environment !== "production") {
            if (environment === "localhost") {
                this._appConfiguration.testMachine = "dev";
            } else {
                this._appConfiguration.testMachine = `${config.instance} - ${environment}`;
            }
        }
        if (config.userFlow?.environmentKey) {
            this._appConfiguration.userflowEnvironmentKey = config.userFlow.environmentKey;
            if (config.userFlow.contentId)
                this._appConfiguration.userflowContentId = config.userFlow.contentId;
        }

        this._appConfiguration.applicationRoot = config.services.application;

        this._appConfiguration.markReady();
    }

    protected async _fetchUserInfo(): Promise<void> {
        const userProfile = await firstValueFrom(this._authorizationService.getUserProfile());

        this._lgConsole.debug("User profile", userProfile);

        this._userInfo.name = userProfile.displayName;
        this._userInfo.email = userProfile.login;
    }
}
